import type { Problem } from '@mathflat/domain/Problem'
import { PROBLEM_LEVEL_KO } from '@mathflat/domain/Problem'
import type { ScoringResult } from '@mathflat/domain/Scoring'
import type { StudentWorksheetVirtualScoring } from '@mathflat/domain/StudentWorksheet'
import { difference } from 'lodash-es'
import { action, computed, makeAutoObservable, makeObservable, observable } from 'mobx'

import {
  getRefinedKaTexUnit,
  type LocalUserAnswers,
  RequestScoring,
  type ScoringOf,
} from '~/@common/api'
import { localStorageService } from '~/@common/services/storage.service'

export class ProblemScoring<T extends 'WORKSHEET' | 'WORKBOOK'> {
  id: ScoringOf<T>['id']
  contentProblemId: Problem['id']

  private _localAutoScoredAnswer: string | null = null
  private _localUserAnswers: LocalUserAnswers | null = null
  private _localScoring: ScoringResult = 'NONE'
  private _scoring: ScoringOf<T>
  private _scoringSaveStorage: 'LOCAL_STORAGE' | 'SERVER' = 'LOCAL_STORAGE'
  private _handwrittenNoteSaveStorage: 'INDEXED_DB' | 'SERVER' = 'INDEXED_DB'
  handwrittenNoteUrl: string | null = null
  readonly score?: number

  constructor(
    params: {
      handwrittenNoteSaveStorage?: 'INDEXED_DB' | 'SERVER'
      scoringSaveStorage?: 'LOCAL_STORAGE' | 'SERVER'
      score?: number
      scoring: ScoringOf<T>
    },
    contentProblemId: Problem['id'],
    handwrittenNoteUrl: string | null = null,
    virtualScores?: StudentWorksheetVirtualScoring['virtualScores'],
  ) {
    this.id = params.scoring.id
    this._scoring = params.scoring
    this._scoringSaveStorage = params.scoringSaveStorage || 'LOCAL_STORAGE'
    this._handwrittenNoteSaveStorage = params.handwrittenNoteSaveStorage || 'INDEXED_DB'
    this.handwrittenNoteUrl = handwrittenNoteUrl
    this.score = params.score

    this._setLocalUserAnswerUnformatted(virtualScores)

    makeObservable<
      typeof this,
      | '_localAutoScoredAnswer'
      | '_localUserAnswers'
      | '_localScoring'
      | '_setLocalUserAnswerUnformatted'
    >(this, {
      _localAutoScoredAnswer: observable,
      _localUserAnswers: observable,
      _localScoring: observable,
      handwrittenNoteUrl: observable,
      localUserInput: computed,
      setLocalUserInput: action,
      setLocalUserAnswers: action,
      isAutoScoring: computed,
      needsTeacherScoring: computed,
      자동채점된답: computed,
      scoringType: computed,
      _setLocalUserAnswerUnformatted: action,
      handwrittenNoteSaveStorage: computed,
    })

    this.contentProblemId = contentProblemId
  }
  get handwrittenNoteSaveStorage() {
    return this._handwrittenNoteSaveStorage
  }
  get problem() {
    return this._scoring.problem as ScoringOf<T>['problem']
  }
  get problemId() {
    if ('problemId' in this._scoring.problem) {
      return this._scoring.problem.problemId
    } else {
      return this._scoring.problem.id
    }
  }
  // 자동채점여부
  get isAutoScoring() {
    return this.scoringType === '자동채점'
  }

  get scoringType() {
    return this._scoring.autoScorable ? '자동채점' : '일반채점'
  }

  /** 자동채점 학습물인데 이 문항은 자동채점이 불가능해 학생이 답을 넣지 못한다 — 선생님이 채점한다 */
  get needsTeacherScoring() {
    return this.isAutoScoring && !this._scoring.problemAutoScorable
  }

  get contentSchoolType() {
    return this._scoring.schoolType
  }

  get workbookProblemId() {
    return this._scoring.kind === 'WORKBOOK' ? this._scoring.workbookProblemId : undefined
  }

  get isSubmitted() {
    return this._scoring.isSubmitted
  }

  get 채점결과() {
    return this._scoring.result
  }

  get 오답_모르는문제여부() {
    return this._scoring.isWrongOrUnknown
  }

  get 난이도() {
    return PROBLEM_LEVEL_KO[this._scoring.problem.level]
  }

  get keypadTypes() {
    return this._scoring.problem.keypadTypes
  }

  get answerData() {
    return this._scoring.problem.answerData
  }

  get answerCount() {
    return this._scoring.problem.answerCount
  }

  get answerUnits() {
    return this._scoring.answerUnits
  }

  /** CMS 워싱으로 학생앱 문항이 교재 원본과 달라진 문항 여부. */
  get 교재불일치여부(): boolean {
    const problem = this._scoring.problem
    return 'modifiedFromOriginalFlag' in problem && problem.modifiedFromOriginalFlag === true
  }

  get teacherScoringFlag() {
    return this._scoring.kind === 'EXAM' ? this._scoring.teacherScoringFlag : false
  }

  get 문제n지선다() {
    return this._scoring.problem.optionCount
  }

  get 문제정답길이() {
    return this._scoring.answerLength
  }

  get 제출한답() {
    return this._scoring.submittedAnswer
  }

  /** 화면에 보여 줄 제출한 답. 시스템이 자동채점한 문항에만 있다 — 일반채점·선생님 채점 문항은 답을 보여 주지 않는다 */
  get 자동채점된답() {
    return this.isAutoScoring && !this.needsTeacherScoring ? this.제출한답 : null
  }

  get 문제이미지() {
    const { problem } = this._scoring
    return 'problemImageUrl' in problem ? problem.problemImageUrl : undefined
  }

  get 문제정답타입() {
    return this._scoring.problem.type
  }

  get 문제정답이미지() {
    const { problem } = this._scoring
    return 'answerImageUrl' in problem ? problem.answerImageUrl : undefined
  }

  get 문제해설이미지() {
    const { problem } = this._scoring
    return 'solutionImageUrl' in problem ? problem.solutionImageUrl : undefined
  }

  get 문제번호() {
    return this._scoring.label
  }

  get 문제정답() {
    return this._scoring.problem.answer
  }
  get 학제학년학기() {
    return this._scoring.gradeLabel
  }
  // 외부에서 localUserInput 접근할 때
  get localUserInput() {
    // 자동채점이고, 답이 입력 되었을 때
    if (this.isAutoScoring) {
      return new RequestScoring.자동채점<T>({
        scoring: this._scoring,
        localUserAnswer: this._localAutoScoredAnswer,
        localUserAnswers: this._localUserAnswers,
        localUnknown: this._localAutoScoredAnswer === 'UNKNOWN',
      })
    }

    return new RequestScoring.일반채점({
      scoring: this._scoring,
      localResult: this._localScoring,
    })
  }

  // 서버에서 받아온 가채점 데이터를 정답 인풋에 맞게 변환
  private _setLocalUserAnswerUnformatted(
    virtualScores: StudentWorksheetVirtualScoring['virtualScores'] | undefined,
  ) {
    // 출제 뒤 자동채점 불가로 바뀐 문항은 그 전에 넣어 둔 답을 복원하지 않는다 — 입력이 없는
    // 문항이 제출 대상으로 세어지고 서버가 받아도 버린다.
    if (this.needsTeacherScoring) return

    const virtualScore = virtualScores?.find((item) => item.worksheetProblemId === this.id)

    // 다중 셀 답 복원 — 전송 시 null 로 보냈던 미입력 빈칸은 UI 상태('')로 되돌린다.
    // 모름은 userAnswers 없이 userAnswer('UNKNOWN' 마커)로 내려와 아래 단일 경로에서 복원된다.
    const storedUserAnswers =
      this._scoringSaveStorage === 'LOCAL_STORAGE'
        ? localStorageService.scoringUserAnswers.get(this.id)
        : virtualScore?.userAnswers?.map((cell) => cell ?? '')
    if (storedUserAnswers?.length) {
      if (this._scoring.isSubmitted) return
      this._localUserAnswers = storedUserAnswers
      return
    }

    let localAnswer =
      this._scoringSaveStorage === 'LOCAL_STORAGE'
        ? localStorageService.scoring.get(this.id)
        : virtualScore?.userAnswer

    if (!localAnswer) return
    if (this._scoring.autoScorable) {
      if (this._scoring.isSubmitted) return
      if (this._scoring.problem.type !== 'SHORT_ANSWER') {
        this._localAutoScoredAnswer = localAnswer
        return
      }

      const unit = this._scoring.answerUnits
      const prefixes = unit && unit.filter((item) => item.index === 0)
      const postfixes = unit && unit.filter((item) => item.index !== 0)
      if (prefixes?.length || postfixes?.length) {
        // 모든 prefix 제거
        prefixes?.forEach((prefix) => {
          const unitText = prefix.unit.match(/\[(.*?)\]/)?.[1] || ''
          localAnswer = localAnswer?.replace(getRefinedKaTexUnit(unitText), '')
        })
        // 모든 postfix 제거
        postfixes?.forEach((postfix) => {
          const unitText = postfix.unit.match(/\[(.*?)\]/)?.[1] || ''
          localAnswer = localAnswer?.replace(getRefinedKaTexUnit(unitText), '')
        })
      }

      this._localAutoScoredAnswer = localAnswer
    } else {
      this._localScoring = localAnswer as ScoringResult
    }
  }

  setLocalUserInput = <T extends 'WORKSHEET' | 'WORKBOOK'>(
    localAnswer: ProblemScoring<T>['_localAutoScoredAnswer'] | ProblemScoring<T>['_localScoring'],
  ) => {
    if (!this.isSubmitted) {
      if (this.isAutoScoring) {
        this._localAutoScoredAnswer = localAnswer
      } else {
        this._localScoring = (
          localAnswer === null ? 'NONE' : localAnswer
        ) as ProblemScoring<T>['_localScoring']
      }

      if (localAnswer === '' || localAnswer === null) {
        localStorageService.removeScoringData({ scoringId: this.id })
      } else {
        if (this._scoringSaveStorage === 'LOCAL_STORAGE') {
          localStorageService.setScoringData({ scoringId: this.id, localAnswer })
        }
      }
      return
    }
    localStorageService.removeScoringData({ scoringId: this.id })
  }

  // 모든 셀이 비어도 배열을 유지한다 — null 로 되돌리면 가채점 페이로드가
  // userAnswers: null 이 되어 마지막 셀을 지웠을 때 서버에 직전 답이 남는다.
  setLocalUserAnswers = (values: LocalUserAnswers) => {
    if (this.isSubmitted) return
    this._localUserAnswers = values

    if (this._scoringSaveStorage !== 'LOCAL_STORAGE') return
    if (values.some((cell) => cell !== '')) {
      localStorageService.setScoringUserAnswers({ scoringId: this.id, userAnswers: values })
    } else {
      localStorageService.removeScoringUserAnswers({ scoringId: this.id })
    }
  }
}

export class ProblemScoringColl<T extends 'WORKSHEET' | 'WORKBOOK'> {
  private _problemScorings: ProblemScoring<T>[]

  constructor(problemScorings: ProblemScoring<T>[]) {
    makeAutoObservable(this)
    this._problemScorings = problemScorings
  }

  get toArr() {
    return this._problemScorings
  }

  get toCorrectArr() {
    return this._problemScorings.filter((problemScoring) => {
      return problemScoring.채점결과 === 'CORRECT'
    })
  }

  get toWrongArr() {
    return this._problemScorings.filter((problemScoring) => {
      return problemScoring.채점결과 === 'WRONG'
    })
  }

  get toNoneArr() {
    return this._problemScorings.filter((problemScoring) => {
      return problemScoring.채점결과 === 'NONE'
    })
  }

  get toUnknowonArr() {
    return this._problemScorings.filter((problemScoring) => {
      return problemScoring.채점결과 === 'UNKNOWN'
    })
  }

  // 학생이 답을 넣어야 하는 문항 — 미채점이면서 선생님 채점 대상이 아닌 것. 입력 완료 판정의 분모다.
  get toAnswerableArr() {
    return this.toNoneArr.filter((problemScoring) => !problemScoring.needsTeacherScoring)
  }

  // 마킹안한 첫번째 문제 인덱스
  get notMarkingProblemFirstIndex() {
    const probemScoringIds = this._problemScorings.map((problemScoring) => problemScoring.id)
    const answerableIds = this.toAnswerableArr.map((problemScoring) => problemScoring.id)
    const localScoringIds = this.toScoredArr.map((scoring) => scoring.id)
    const notMarkingArr = difference(answerableIds, localScoringIds)
    return probemScoringIds.indexOf(notMarkingArr[0])
  }

  // 채점 전 모든 문제의 답안이 모두 입력되었는지에 대한 여부
  get isAllLocallyScored() {
    return this.toScoredArr.length === this.toAnswerableArr.length
  }

  get toWrongOrUnknwonArr() {
    return this._problemScorings.filter((problemScoring) => problemScoring.오답_모르는문제여부)
  }

  // 로컬 채점된 배열
  get toScoredArr() {
    return this._problemScorings.filter(
      (problemScoring) => !problemScoring.localUserInput.isNotLocallyScored,
    )
  }

  // 하나도 채점되지 않았을 경우
  get isNothingSubmitted() {
    return this.toNoneArr.length === this.toArr.length
  }

  get gradingCount() {
    const { correct, incorrect, unknown, none } = this.toArr.reduce(
      (accumulator, { 채점결과 }) => {
        switch (채점결과) {
          case 'CORRECT':
            return { ...accumulator, correct: accumulator.correct + 1 }
          case 'WRONG':
            return { ...accumulator, incorrect: accumulator.incorrect + 1 }
          case 'UNKNOWN':
            return { ...accumulator, unknown: accumulator.unknown + 1 }
          case 'RESTRICTED':
          case 'NONE':
            return { ...accumulator, none: accumulator.none + 1 }
          default:
            return accumulator
        }
      },
      { correct: 0, incorrect: 0, unknown: 0, none: 0, score: 0 },
    )
    return { correct, incorrect, unknown, none }
  }

  onAllCheckCorrect() {
    this._problemScorings.forEach((problemScoring) => {
      problemScoring.setLocalUserInput('CORRECT')
    })
  }

  onAllClear = (cleanUpFn?: (problemScorings: ProblemScoring<T>[]) => void) => {
    this._problemScorings.forEach((problemScoring) => {
      problemScoring.setLocalUserInput(null)
      // 단일 답 문항까지 [] 로 바꾸면 이후 입력이 다중 셀 기준으로 판정돼 미입력이 된다 — 다중 셀 문항만 비운다.
      const { localUserAnswers } = problemScoring.localUserInput as RequestScoring.자동채점<T>
      if (localUserAnswers != null) problemScoring.setLocalUserAnswers([])
    })

    if (cleanUpFn) {
      cleanUpFn(this._problemScorings)
    }
  }
}
